import React, { useLayoutEffect, useRef, useState } from 'react';
import { getCellKey } from '../gridUtils';
import '../styles/Grid.css';

function Grid({ grid, onPlaceTile, onRemoveTile, hand, onMoveTile, selectedLetter, validationState = {}, validationOverlays = [] }) {
  const [draggedCell, setDraggedCell] = useState(null);
  const [touchDrag, setTouchDrag] = useState(null);
  const gridRef = useRef(null);
  const suppressClickRef = useRef(false);
  const [gridMetrics, setGridMetrics] = useState({
    cellSize: 40,
    cellGap: 4,
    gridPadding: 16
  });

  console.log('GRID: Rendering with validationState keys:', Object.keys(validationState));
  Object.keys(grid).forEach(key => {
    const val = validationState[key];
    if (val) {
      console.log(`GRID: Cell ${key} = ${val.valid ? 'VALID' : 'INVALID'}`);
    }
  });

  // Calculate grid bounds: start with 7x7, expand if tiles placed outside
  const calculateBounds = () => {
    const keys = Object.keys(grid);

    if (keys.length === 0) {
      // No tiles placed yet, show 7x7 centered at 0,0
      return { minRow: -3, maxRow: 3, minCol: -3, maxCol: 3 };
    }

    // Keep the initial board in place, expanding only when tiles need room.
    let minRow = -3, maxRow = 3;
    let minCol = -3, maxCol = 3;

    keys.forEach((key) => {
      const parts = key.split(',');
      const row = parseInt(parts[0], 10);
      const col = parseInt(parts[1], 10);
      minRow = Math.min(minRow, row - 1);
      maxRow = Math.max(maxRow, row + 1);
      minCol = Math.min(minCol, col - 1);
      maxCol = Math.max(maxCol, col + 1);
    });

    return { minRow, maxRow, minCol, maxCol };
  };

  const bounds = calculateBounds();
  const minRow = bounds.minRow;
  const maxRow = bounds.maxRow;
  const minCol = bounds.minCol;
  const maxCol = bounds.maxCol;

  const handleCellClick = (row, col) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    const key = getCellKey(row, col);
    const cellLetter = grid[key];

    if (cellLetter) {
      // Click on occupied cell = remove
      onRemoveTile(row, col);
    } else if (selectedLetter) {
      // Click on empty cell with letter selected = place
      onPlaceTile(row, col, selectedLetter);
    }
  };

  const handleDragStart = (e, row, col) => {
    // Touch placement is handled by pointer events, never by native dragging.
    if (touchDrag) {
      e.preventDefault();
      return;
    }
    const key = getCellKey(row, col);
    const letter = grid[key];
    if (letter) {
      e.dataTransfer.effectAllowed = 'move';
      setDraggedCell({ row, col, letter });
      e.dataTransfer.setData('type', 'grid-tile');
      e.dataTransfer.setData('row', String(row));
      e.dataTransfer.setData('col', String(col));
      e.dataTransfer.setData('letter', letter);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, row, col) => {
    e.preventDefault();

    if (e.dataTransfer.getData('type') === 'grid-tile' && draggedCell) {
      // Moving tile from grid to grid
      const key = getCellKey(row, col);
      if (!grid[key]) {
        // Destination is empty
        onMoveTile?.(draggedCell.row, draggedCell.col, row, col);
      } else if (grid[key] !== undefined) {
        // Cell is occupied - reject the move
        console.log('Cannot move to occupied cell');
      }
      setDraggedCell(null);
    } else if (e.dataTransfer.getData('type') === 'hand-tile') {
      // Moving tile from hand to grid
      const letter = e.dataTransfer.getData('letter');
      onPlaceTile(row, col, letter);
    }
  };

  const handlePointerDown = (e, row, col) => {
    if (e.pointerType === 'mouse') {
      suppressClickRef.current = false;
      return;
    }
    if (!e.isPrimary) return;
    suppressClickRef.current = false;

    const key = getCellKey(row, col);
    const letter = grid[key];
    if (!letter) return;

    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setTouchDrag({
      row,
      col,
      letter,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      x: e.clientX,
      y: e.clientY
    });
  };

  const handlePointerMove = (e) => {
    if (!touchDrag || e.pointerId !== touchDrag.pointerId) return;

    e.preventDefault();
    setTouchDrag((drag) => ({
      ...drag,
      x: e.clientX,
      y: e.clientY
    }));
  };

  const finishPointerDrag = (e) => {
    if (!touchDrag || e.pointerId !== touchDrag.pointerId) return;

    e.preventDefault();
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    suppressClickRef.current = true;

    const dropTarget = document.elementFromPoint(e.clientX, e.clientY);
    const gridTarget = dropTarget?.closest('[data-grid-cell="true"]');
    const trayTarget = dropTarget?.closest('[data-letter-tray="true"]');

    if (gridTarget) {
      const toRow = Number(gridTarget.dataset.row);
      const toCol = Number(gridTarget.dataset.col);
      const targetKey = getCellKey(toRow, toCol);
      const fromKey = getCellKey(touchDrag.row, touchDrag.col);

      if (Number.isFinite(toRow) && Number.isFinite(toCol) && targetKey !== fromKey && !grid[targetKey]) {
        onMoveTile?.(touchDrag.row, touchDrag.col, toRow, toCol);
      } else if (targetKey === fromKey && Math.hypot(e.clientX - touchDrag.startX, e.clientY - touchDrag.startY) <= 8) {
        onRemoveTile(touchDrag.row, touchDrag.col);
      }
    } else if (trayTarget) {
      onRemoveTile(touchDrag.row, touchDrag.col);
    }

    setTouchDrag(null);
  };

  const cancelPointerDrag = (e) => {
    if (!touchDrag || e.pointerId !== touchDrag.pointerId) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    suppressClickRef.current = true;
    setTouchDrag(null);
  };

  const cells = [];
  for (let row = minRow; row <= maxRow; row++) {
    for (let col = minCol; col <= maxCol; col++) {
      const key = getCellKey(row, col);
      const letter = grid[key];
      const validation = validationState[key];

      let cellClass = `grid-cell ${letter ? 'filled' : 'empty'}`;
      if (validation) {
        cellClass += ` validated ${validation.status || (validation.valid ? 'valid' : 'invalid')}`;
      }

      cells.push(
        <div
          key={key}
          className={cellClass}
          data-grid-cell="true"
          data-row={row}
          data-col={col}
          onClick={() => handleCellClick(row, col)}
          onDragStart={(e) => handleDragStart(e, row, col)}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, row, col)}
          onPointerDown={(e) => handlePointerDown(e, row, col)}
          onPointerMove={handlePointerMove}
          onPointerUp={finishPointerDrag}
          onPointerCancel={cancelPointerDrag}
          draggable={!!letter && !touchDrag}
        >
          {letter && <span className="cell-letter">{letter}</span>}
        </div>
      );
    }
  }

  const gridWidth = maxCol - minCol + 1;

  useLayoutEffect(() => {
    const updateGridMetrics = () => {
      if (!gridRef.current) return;

      const computed = window.getComputedStyle(gridRef.current);
      const firstCell = gridRef.current.querySelector('.grid-cell');
      setGridMetrics({
        cellSize: firstCell?.getBoundingClientRect().width || 40,
        cellGap: parseFloat(computed.gap) || 4,
        gridPadding: parseFloat(computed.paddingLeft) || 16
      });
    };

    updateGridMetrics();
    window.addEventListener('resize', updateGridMetrics);
    return () => window.removeEventListener('resize', updateGridMetrics);
  }, [gridWidth, minRow, maxRow, minCol, maxCol]);

  const overlayStyleFor = (overlay) => {
    const rows = overlay.cells.map((cell) => cell.row);
    const cols = overlay.cells.map((cell) => cell.col);
    const minOverlayRow = Math.min(...rows);
    const maxOverlayRow = Math.max(...rows);
    const minOverlayCol = Math.min(...cols);
    const maxOverlayCol = Math.max(...cols);
    const width = (maxOverlayCol - minOverlayCol + 1) * gridMetrics.cellSize + (maxOverlayCol - minOverlayCol) * gridMetrics.cellGap;
    const height = (maxOverlayRow - minOverlayRow + 1) * gridMetrics.cellSize + (maxOverlayRow - minOverlayRow) * gridMetrics.cellGap;
    const left = gridMetrics.gridPadding + (minOverlayCol - minCol) * (gridMetrics.cellSize + gridMetrics.cellGap);
    const top = gridMetrics.gridPadding + (minOverlayRow - minRow) * (gridMetrics.cellSize + gridMetrics.cellGap);

    return {
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`
    };
  };

  return (
    <div className="grid-container">
      <div
        ref={gridRef}
        className="grid"
        style={{
          gridTemplateColumns: `repeat(${gridWidth}, var(--grid-cell-size))`
        }}
      >
        {validationOverlays.map((overlay) => (
          <div
            key={overlay.key}
            className={`word-outline ${overlay.status}`}
            style={overlayStyleFor(overlay)}
          />
        ))}
        {cells}
      </div>

      {touchDrag && (
        <div
          className="grid-touch-drag-tile"
          style={{
            left: touchDrag.x,
            top: touchDrag.y
          }}
        >
          {touchDrag.letter}
        </div>
      )}

      {selectedLetter && (
        <div className="selected-hint">
          Selected: <span className="hint-letter">{selectedLetter}</span>
          <span className="hint-text">Click a cell to place</span>
        </div>
      )}
    </div>
  );
}

export default Grid;
